import { memo, useCallback, useMemo } from 'react';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Link, matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { MantineTabProps } from '@dealstack/frontend/shared/components/MantineTabs/MantineTab.types.ts';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import {
	mipManagePath,
	mipManagePathPattern,
} from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { PageContainer } from '@dealstack/frontend/shared/layout/PageContainer/PageContainer.tsx';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { ActionIcon, Flex } from '@mantine/core';
import { MantineTabs } from '@dealstack/frontend/shared/components/MantineTabs/MantineTabs.tsx';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext.ts';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { AddBreadcrumbItem } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.types.ts';
import { useMipManageBreadcrumbsPrefix } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/utils/mipManageBreadcrumbs.utils.ts';

export const MipManageTabsLayout = memo(() => {
	const tMipManage = useTranslationMipManage();
	const navigate = useNavigate();
	const location = useLocation();
	const { mipData, MIPManageTabs, dealId, mipId } = useMIPManageContext();

	const tabsConfig = useMemo<MantineTabProps<MIP_MANAGE_ROUTES>[]>(
		() =>
			MIPManageTabs.map((tab) => ({
				...tab,
				label: tMipManage(tab.label),
			})),
		[MIPManageTabs, tMipManage]
	);

	const activeTab = useMemo(
		() =>
			tabsConfig.find((tab) =>
				matchPath({ path: mipManagePathPattern(tab.value), end: false }, location.pathname)
			),
		[location.pathname, tabsConfig]
	);

	// A tab's own page matches its path exactly; anything deeper is a detail page that
	// owns its breadcrumbs (and appends the record it is showing), so this layout keeps
	// out of the way rather than emitting a second, shorter trail alongside it.
	const isDetailRoute = useMemo(
		() =>
			!tabsConfig.some((tab) =>
				matchPath({ path: mipManagePathPattern(tab.value), end: true }, location.pathname)
			),
		[location.pathname, tabsConfig]
	);

	const tabValue = activeTab?.value ?? MIP_MANAGE_ROUTES.INVESTORS;
	const programmeName = mipData?.programmeName ?? '';

	const breadcrumbsPrefix = useMipManageBreadcrumbsPrefix({ dealId, mipId, programmeName });

	const breadcrumbs = useMemo<AddBreadcrumbItem[]>(
		() =>
			isDetailRoute
				? []
				: [
						...breadcrumbsPrefix,
						{
							name: activeTab?.label ?? '',
							url: '',
							noLink: true,
							hideInAppHeader: true,
						},
					],
		[isDetailRoute, breadcrumbsPrefix, activeTab?.label]
	);

	useBreadcrumbs(breadcrumbs);

	const onTabChange = useCallback(
		(value: string | null) => {
			if (!value) {
				return;
			}
			navigate(mipManagePath(value, { dealId, mipId }));
		},
		[dealId, mipId, navigate]
	);

	const settingLink = mipManagePath(MIP_MANAGE_ROUTES.SETTINGS, { dealId, mipId });

	return (
		<PageContainer style={{ overflow: 'hidden', position: 'relative' }}>
			<PageHeader pageTitle={programmeName} hideBreadcrumbs>
				<Link
					to={settingLink}
					data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_PROJECT_SETTINGS_BUTTON}
				>
					<ActionIcon radius="50%" size="xl" c="dark.8">
						<Icon variant="gear" size="xl" />
					</ActionIcon>
				</Link>
			</PageHeader>

			<Flex direction="column" style={{ flex: 1, minHeight: 0 }}>
				<MantineTabs
					tabs={tabsConfig}
					panels={[]}
					value={tabValue}
					onTabChange={onTabChange}
				>
					<Flex style={{ overflow: 'hidden' }} p="1rem">
						<Outlet />
					</Flex>
				</MantineTabs>
			</Flex>
		</PageContainer>
	);
});
